// Cliente API base
import { API_ENDPOINTS } from '../config';

// Función helper para hacer peticiones HTTP que evita completamente la interceptación de rrweb
const makeRequest = async (url, options = {}) => {
  // Intentar usar el fetch original de rrweb primero
  const safeFetch = (typeof window !== 'undefined' && window.__rrweb_originalFetch) || fetch;
  
  // Función helper para hacer fallback a XMLHttpRequest
  const isFormData = options.body instanceof FormData;
  const fallbackToXHR = () => {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', url, true);
        
        // Configurar headers (no establecer Content-Type para FormData)
        if (options.headers) {
          Object.keys(options.headers).forEach(key => {
            // No establecer Content-Type para FormData, el navegador lo hará automáticamente
            if (isFormData && key.toLowerCase() === 'content-type') {
              return;
            }
            try {
              xhr.setRequestHeader(key, options.headers[key]);
            } catch (e) {
              // Ignorar errores de headers inválidos
            }
          });
        }
        
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            statusText: xhr.statusText,
            text: xhr.responseText,
            headers: {
              get: (name) => xhr.getResponseHeader(name),
            },
          });
        };
        
        xhr.onerror = () => {
          reject(new Error('Error de red'));
        };
      
      xhr.ontimeout = () => {
        reject(new Error('Timeout de red'));
      };
      
      // Configurar timeout si existe
      if (options.timeout) {
        xhr.timeout = options.timeout;
      }
        
        xhr.send(options.body || null);
      });
  };
  
  try {
    const response = await safeFetch(url, options);
    
    // Intentar leer el body - aquí es donde puede fallar en Safari con rrweb
    let text;
    try {
      text = await response.text();
    } catch (textError) {
      // Si response.text() falla por body bloqueado/disturbed, usar XMLHttpRequest
      const errorMessage = textError?.message || '';
      const errorName = textError?.name || '';
      const errorString = (errorMessage + ' ' + errorName).toLowerCase();
      
      // Detectar errores relacionados con body bloqueado/disturbed/locked
      if (
        errorString.includes('disturbed') ||
        errorString.includes('locked') ||
        errorString.includes('body') ||
        errorString.includes('clone') ||
        errorString.includes('already used')
      ) {
        console.warn('⚠️ Body bloqueado por rrweb. Usando XMLHttpRequest como fallback...', {
          error: errorMessage,
          status: response.status,
        });
        
        // Hacer fallback a XMLHttpRequest para leer el body correctamente
        return fallbackToXHR();
      }
      
      // Si es otro tipo de error, lanzarlo
      throw textError;
    }
    
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      text: text,
      headers: response.headers,
    };
  } catch (error) {
    // Si el error es sobre clonar o body usado en el fetch inicial, usar XMLHttpRequest como fallback
    const errorMessage = error?.message || '';
    const errorName = error?.name || '';
    const errorString = (errorMessage + ' ' + errorName).toLowerCase();
    
    if (
      errorString.includes('clone') || 
      errorString.includes('body is already used') ||
      errorString.includes('body used') ||
      errorString.includes('disturbed') ||
      errorString.includes('locked')
    ) {
      console.warn('⚠️ rrweb interceptó fetch. Usando XMLHttpRequest como fallback...');
      return fallbackToXHR();
    }
    
    throw error;
  }
};

class ApiClient {
  async request(url, options = {}) {
    // Obtener token de autenticación si existe
    const token = localStorage.getItem('auth_token');
    
    const isFormData = options.body instanceof FormData;
    
    const headers = {
      'Accept': 'application/json',
      ...options.headers,
    };

    // No establecer Content-Type para FormData, el navegador lo hará automáticamente
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log('🔵 API Request:', { url, method: options.method || 'GET' });
    
    // Variable para almacenar la respuesta
    let response;
    let responseText = '';
    
    try {
      // Usar makeRequest que maneja automáticamente la interceptación de rrweb
      // Para FormData, usar el body directamente; para JSON, stringify aquí
      const body = isFormData
        ? options.body
        : (options.body !== undefined ? JSON.stringify(options.body) : undefined);
      
      response = await makeRequest(url, {
        method: options.method || 'GET',
        headers,
        body,
      });

      console.log('🔵 API Response status:', response.status, response.statusText);

      // La respuesta ya viene con el texto leído
      responseText = response.text;
      console.log('🔵 API Response text length:', responseText.length);
      
    } catch (networkError) {
      // Error de red
      console.error('❌ Network Error:', networkError);
      throw new Error('Error de conexión. Verifica tu internet.');
    }
    
    // Parsear como JSON si hay contenido
    let data = null;
    if (responseText && responseText.trim()) {
      try {
        data = JSON.parse(responseText);
        console.log('🔵 API Response parsed as JSON:', data);
      } catch (parseError) {
        // Si no es JSON válido, crear un objeto de error
        console.error('❌ Error parsing JSON:', parseError);
        const error = new Error(`Error al procesar la respuesta del servidor: ${response.statusText}`);
        error.status = response ? response.status : 0;
        error.responseText = responseText.substring(0, 200);
        throw error;
      }
    } else {
      // Respuesta vacía
      const error = new Error(`Error al procesar la respuesta del servidor: ${response ? response.statusText : 'Sin respuesta'}`);
      error.status = response ? response.status : 0;
      throw error;
    }
    
    // Verificar si hay errores HTTP o en la respuesta
    // Cuando el backend retorna success: false, lanzamos un error
    if (response && (!response.ok || (data && data.success === false))) {
      // Extraer mensaje de error del backend
      let errorMessage = (data && data.error) || (data && data.message) || `Error HTTP ${response.status}`;
      
      // Si hay errores de validación, formatearlos mejor
      if (data && data.errors && typeof data.errors === 'object') {
        const errorMessages = [];
        Object.keys(data.errors).forEach(key => {
          const fieldErrors = Array.isArray(data.errors[key]) ? data.errors[key] : [data.errors[key]];
          fieldErrors.forEach(msg => errorMessages.push(`${key}: ${msg}`));
        });
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(', ');
        }
      }
      
      const error = new Error(errorMessage);
      // Agregar información adicional del error
      error.status = response.status;
      error.errors = (data && data.errors) || null;
      error.data = data; // Incluir los datos completos del error para debugging
      console.error('❌ API Error:', {
        message: errorMessage,
        status: response.status,
        data: data
      });
      throw error;
    }

    console.log('✅ API Success:', data);
    return data;
  }

  async get(url, params = {}) {
    /*
      Los arreglos van como `clave[]=3&clave[]=7`, que es lo que Laravel lee
      como arreglo.

      `new URLSearchParams({store_ids: [3,7]})` los aplana a «3,7» y PHP recibe
      un texto, no una lista: `intval("3,7")` es 3. El filtro por varias tiendas
      de Canjes QR se quedaba con la primera y descartaba el resto **en
      silencio** — la pantalla contestaba, con datos incompletos, que es la peor
      forma de fallar.
    */
    const consulta = new URLSearchParams();
    Object.entries(params).forEach(([clave, valor]) => {
      if (Array.isArray(valor)) {
        valor.forEach((v) => consulta.append(`${clave}[]`, v));
      } else {
        consulta.append(clave, valor);
      }
    });

    const queryString = consulta.toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.request(fullUrl, { method: 'GET' });
  }

  async post(url, data) {
    return this.request(url, {
      method: 'POST',
      body: data,
    });
  }

  async put(url, data) {
    const isFormData = data instanceof FormData;

    // Para PUT con FormData, Laravel requiere _method=PUT en el FormData
    // y que el método HTTP real sea POST para que PHP/Laravel carguen $_FILES
    if (isFormData) {
      data.append('_method', 'PUT');
      return this.request(url, {
        method: 'POST',
        body: data,
      });
    }

    return this.request(url, {
      method: 'PUT',
      body: data,
    });
  }

  async delete(url) {
    return this.request(url, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient();
